import { useState } from "react";
import { useTranslation } from "../i18n";
import { ProviderMark } from "./icons";
import { Alert, Button } from "./ui";
import { ApiError } from "../lib/api";
import { useGitConnections, useGitProviders, useSigninIdentities, useStartGitAuth } from "../lib/queries";
import type { GitProviderId } from "../lib/types";

/**
 * "You signed in with GitHub -- connect it to deploy."
 *
 * Signing in and connecting repositories are two consents on the same
 * GitHub or GitLab application, on purpose: the sign-in token can read the
 * person and nothing else, and the repository token is the organization's.
 * The second consent is a surprise only if nothing says it is coming; this
 * says so, names the account, and starts it in one click. It renders
 * nothing unless the person signed in with a host this organization has
 * not connected -- a password-only account never sees it.
 */
/** The host the person signed in with that this organization has not connected, if any. */
export function useSigninHostCandidate(orgId: string | undefined) {
  const identities = useSigninIdentities();
  const providers = useGitProviders(orgId);
  const connections = useGitConnections(orgId);
  const connected = new Set((connections.data ?? []).map((c) => c.provider));
  const configured = new Set((providers.data ?? []).filter((p) => p.configured).map((p) => p.id));
  return (identities.data ?? []).find((i) => configured.has(i.provider) && !connected.has(i.provider)) ?? null;
}

export function ConnectSigninHost({
  orgId,
  canManage,
  redirectTo,
  compact = false,
}: {
  orgId: string | undefined;
  canManage: boolean;
  /** Where the host sends the person back to after consenting. */
  redirectTo: string;
  /** Inside another panel: a sentence and a button, no frame of its own. */
  compact?: boolean;
}) {
  const { t } = useTranslation("settings");
  const candidate = useSigninHostCandidate(orgId);
  const start = useStartGitAuth(orgId ?? "");
  const [error, setError] = useState<string | null>(null);
  if (!orgId || !canManage || !candidate) return null;

  const label = candidate.provider === "github" ? "GitHub" : "GitLab";
  async function connect(provider: GitProviderId) {
    setError(null);
    try {
      const { authorizeUrl } = await start.mutateAsync({ provider, redirectTo });
      window.location.assign(authorizeUrl);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("integrations.startFailed"));
    }
  }

  /* Mark and sentence on one row; the button beside them from `sm` up and
     on its own row below it on a phone, where a column beside a button
     squeezes the sentence into a ribbon. */
  const body = (
    <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2.5 sm:grid-cols-[auto_1fr_auto]">
      <ProviderMark provider={candidate.provider} className={`size-5 ${candidate.provider === "gitlab" ? "text-[#fc6d26]" : "text-fg"}`} />
      <p className="min-w-0 text-sm leading-6 text-fg">
        {t("integrations.signedInWith", { provider: label, login: candidate.login })}{" "}
        <span className="text-fg-muted">{t("integrations.signedInWithLead")}</span>
      </p>
      <div className="col-span-2 sm:col-span-1">
        <Button size="sm" loading={start.isPending} onClick={() => void connect(candidate.provider)} className="w-full sm:w-auto">
          {t("integrations.connectProvider", { provider: label })}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-2">
      {error && <Alert>{error}</Alert>}
      {compact ? body : <div className="rounded-md border border-accent/30 bg-accent/5 px-4 py-3">{body}</div>}
    </div>
  );
}
